// src/components/Enrollment/PromotionPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";

import { getGrades } from "../../api/gradeApi";
import { getSessions } from "../../api/sessionApi";
import {
  getPromotionCandidates,
  executePromotion,
} from "../../api/enrollmentApi";

const ACTION_OPTIONS = [
  { label: "Promote", value: "promote" },
  { label: "Repeat", value: "repeat" },
  { label: "Graduate", value: "graduate" },
];

const asArray = (resp) => (Array.isArray(resp) ? resp : resp?.data || []);

const PromotionPage = () => {
  const [grades, setGrades] = useState([]);
  const [sessions, setSessions] = useState([]);

  const [fromSession, setFromSession] = useState(null);
  const [fromGrade, setFromGrade] = useState(null);
  const [toSession, setToSession] = useState(null);

  const [nextGrade, setNextGrade] = useState(null);
  const [rows, setRows] = useState([]); // { student_id, full_name, enrollment_no, enrollment_id, action, to_grade_id }
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setGrades(asArray(await getGrades()));
        const s = asArray(await getSessions());
        setSessions(s);
        const active = s.find((x) => x.is_active);
        if (active) setFromSession(active.id);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load grades/sessions");
      }
    })();
  }, []);

  const gradeOptions = useMemo(
    () => grades.map((g) => ({ label: g.name, value: g.id })),
    [grades]
  );
  const sessionOptions = useMemo(
    () => sessions.map((s) => ({ label: s.name, value: s.id })),
    [sessions]
  );

  const loadCandidates = async () => {
    if (!fromSession || !fromGrade) {
      toast.warn("Select a source session and grade first.");
      return;
    }
    setLoading(true);
    try {
      const data = await getPromotionCandidates(fromSession, fromGrade);
      const next = data.next_grade;
      setNextGrade(next);
      const defaultAction = data.from_grade?.is_final_year ? "graduate" : "promote";
      setRows(
        data.students.map((s) => ({
          ...s,
          action: defaultAction,
          to_grade_id: next?.id || null,
        }))
      );
      setSelected([]);
      if (!data.students.length) toast.info("No active students in this grade/session.");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load candidates");
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (studentId, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.student_id === studentId ? { ...r, ...patch } : r))
    );

  const setAllAction = (action) =>
    setRows((prev) => prev.map((r) => ({ ...r, action })));

  const actionBody = (row) => (
    <Dropdown
      value={row.action}
      options={ACTION_OPTIONS}
      onChange={(e) => updateRow(row.student_id, { action: e.value })}
      style={{ width: "10rem" }}
    />
  );

  const targetBody = (row) => {
    if (row.action === "graduate") return <Tag value="Graduating" severity="warning" />;
    if (row.action === "repeat")
      return <span className="text-color-secondary">Same grade (repeat)</span>;
    return (
      <Dropdown
        value={row.to_grade_id}
        options={gradeOptions}
        onChange={(e) => updateRow(row.student_id, { to_grade_id: e.value })}
        placeholder="Select target grade"
        filter
        style={{ width: "12rem" }}
      />
    );
  };

  const runPromotion = () => {
    if (!toSession) {
      toast.warn("Select the target session to promote into.");
      return;
    }
    if (!selected.length) {
      toast.warn("Select at least one student.");
      return;
    }
    // validate promote rows have a target grade
    const missing = selected.find((r) => r.action === "promote" && !r.to_grade_id);
    if (missing) {
      toast.error(`Select a target grade for ${missing.full_name}.`);
      return;
    }

    const counts = selected.reduce(
      (acc, r) => ((acc[r.action] = (acc[r.action] || 0) + 1), acc),
      {}
    );
    confirmDialog({
      header: "Confirm Promotion",
      message: `Apply to ${selected.length} student(s)? ` +
        `Promote: ${counts.promote || 0}, Repeat: ${counts.repeat || 0}, Graduate: ${counts.graduate || 0}. ` +
        `Their current enrollment will be marked completed.`,
      icon: "pi pi-exclamation-triangle",
      accept: doPromotion,
    });
  };

  const doPromotion = async () => {
    setSaving(true);
    try {
      const payload = {
        to_session_id: toSession,
        items: selected.map((r) => ({
          student_id: r.student_id,
          enrollment_id: r.enrollment_id,
          action: r.action,
          to_grade_id: r.action === "promote" ? r.to_grade_id : undefined,
        })),
      };
      const resp = await executePromotion(payload);
      toast.success(resp?.message || "Promotion complete");
      // Remove processed students from the list
      const done = new Set(selected.map((r) => r.student_id));
      setRows((prev) => prev.filter((r) => !done.has(r.student_id)));
      setSelected([]);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Promotion failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-3">
      <ConfirmDialog />
      <h2 className="mb-1">Student Promotions</h2>
      <p className="text-color-secondary mt-0">
        Promote a whole class to the next grade/session, repeat, or graduate final-year students.
      </p>

      {/* Source selection */}
      <div className="card p-3 mb-3">
        <div className="p-fluid formgrid grid">
          <div className="field col-12 md:col-4">
            <label>From Session *</label>
            <Dropdown
              value={fromSession}
              options={sessionOptions}
              onChange={(e) => setFromSession(e.value)}
              placeholder="Select session"
            />
          </div>
          <div className="field col-12 md:col-4">
            <label>From Grade *</label>
            <Dropdown
              value={fromGrade}
              options={gradeOptions}
              onChange={(e) => setFromGrade(e.value)}
              placeholder="Select grade"
              filter
            />
          </div>
          <div className="field col-12 md:col-4 flex align-items-end">
            <Button
              label="Load Students"
              icon="pi pi-search"
              onClick={loadCandidates}
              loading={loading}
            />
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <div className="card p-3">
          {/* Target + bulk controls */}
          <div className="p-fluid formgrid grid align-items-end mb-2">
            <div className="field col-12 md:col-4">
              <label>Into Session (target) *</label>
              <Dropdown
                value={toSession}
                options={sessionOptions}
                onChange={(e) => setToSession(e.value)}
                placeholder="Select target session"
              />
            </div>
            <div className="field col-12 md:col-4">
              <label>Suggested next grade</label>
              <div>
                {nextGrade ? (
                  <Tag value={nextGrade.name} severity="info" />
                ) : (
                  <Tag value="None (final year)" severity="warning" />
                )}
              </div>
            </div>
            <div className="field col-12 md:col-4">
              <label>Set all actions</label>
              <div className="flex gap-2">
                <Button label="Promote" size="small" outlined onClick={() => setAllAction("promote")} />
                <Button label="Repeat" size="small" outlined onClick={() => setAllAction("repeat")} />
                <Button label="Graduate" size="small" outlined onClick={() => setAllAction("graduate")} />
              </div>
            </div>
          </div>

          <DataTable
            value={rows}
            selection={selected}
            onSelectionChange={(e) => setSelected(e.value)}
            dataKey="student_id"
            className="p-datatable-sm"
            scrollable
            scrollHeight="480px"
            emptyMessage="No students to promote"
          >
            <Column selectionMode="multiple" style={{ width: "3rem" }} />
            <Column field="enrollment_no" header="Enrollment #" style={{ minWidth: "120px" }} />
            <Column field="full_name" header="Student" style={{ minWidth: "160px" }} />
            <Column header="Action" body={actionBody} style={{ minWidth: "11rem" }} />
            <Column header="Target Grade" body={targetBody} style={{ minWidth: "13rem" }} />
          </DataTable>

          <div className="flex justify-content-end mt-3">
            <Button
              label={`Promote ${selected.length} Selected`}
              icon="pi pi-arrow-circle-up"
              onClick={runPromotion}
              loading={saving}
              disabled={!selected.length}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionPage;
